import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {isCustomFieldEnumType} from '../../../../../helpers/customFieldHelpers';
import {renderCustomFieldType} from '../../../../../helpers/customFieldViewHelpers';
import {
  CrmSyncDefaultFieldMappingIdsEnum,
  CrmSyncMappingCustomFieldTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../models/api/crmSyncModel';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
  CustomFieldsEnum,
} from '../../../../../models/api/customFieldModel';
import {CreateCustomFieldDropdownContainer} from '../../../../common/dropdowns/customFields/createCustomFieldDropdownContainer';
import {CustomFieldDropdown} from '../../../../common/dropdowns/customFields/customFieldDropdown';
import {MappingRowField} from '../../../../common/mapping/mappingRowField';
import {renderDefaultCustomFieldName} from '../crmSyncHelpers';

/*
 * Props.
 */

interface CrmCustomFieldDropdownWrapperProps {
  source: CrmSyncSourcesEnum;
  types: ReadonlyArray<CustomFieldsEnum | CrmSyncMappingCustomFieldTypesEnum>;
  customFields: ReadonlyArray<CustomFieldModel>;
  selectedCustomFieldId?: number;
  frontCustomFieldIcon?: IconName;
  frontCustomFieldName?: ReactNode;
  crmCustomFieldName?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isFrontCustomFieldMissing?: boolean;
  allSelectedCustomFieldIds: ReadonlyArray<number>;
  onSelect: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledMappingPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledSourceSpan = styled.span`
  text-transform: capitalize;
`;

/*
 * Component.
 */

export const CrmCustomFieldDropdownWrapper: FC<CrmCustomFieldDropdownWrapperProps> = (props) => {
  const {
    source,
    types,
    selectedCustomFieldId,
    frontCustomFieldIcon,
    frontCustomFieldName,
    crmCustomFieldName,
    isReadOnly,
    isDisabled,
    allSelectedCustomFieldIds,
    isFrontCustomFieldMissing,
    customFields,
    onSelect,
  } = props;

  const hasNoCustomFieldsToShow = customFields.length === 0;
  const [isShowingCreateDropdown, setIsShowingCreateDropdown] = useState(false);
  const [fieldName, setFieldName] = useState(crmCustomFieldName);

  const onCancelClick = (popoverProps: PopoverTransferProps) => {
    if (hasNoCustomFieldsToShow) {
      popoverProps.onRequestClose();
    } else {
      setIsShowingCreateDropdown(false);
    }
  };

  const onCreateClick = (input?: string) => {
    if (input) {
      setFieldName(input);
    } else {
      setFieldName(crmCustomFieldName);
    }

    setIsShowingCreateDropdown(true);
  };

  return (
    <PopoverCoordinator
      isDisabled={isReadOnly || isDisabled}
      renderButton={() => (
        <MappingRowField
          iconName={frontCustomFieldIcon}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          hasError={isFrontCustomFieldMissing}
          message={maybeRenderMessage(selectedCustomFieldId)}
          errorMessage={maybeRenderErrorMessage(isFrontCustomFieldMissing)}
        >
          {frontCustomFieldName ?? renderPlaceholder()}
        </MappingRowField>
      )}
      renderModal={(popoverProps) => {
        if (hasNoCustomFieldsToShow || isShowingCreateDropdown) {
          return (
            <CreateCustomFieldDropdownContainer
              popoverProps={popoverProps}
              types={types.filter(isCustomFieldEnumType)}
              fieldName={fieldName}
              customFieldResourceType={CustomFieldResourceTypesEnum.ACCOUNT}
              renderTypesDisabledTooltip={(type) => renderTypesDisabledTooltip(source, type)}
              renderTypesDropdownItemDisabledTooltip={() => renderTypesDropdownItemDisabledTooltip(source)}
              renderDropdownTitle={renderCreateDropdownTitle}
              isShowingBackButton={!hasNoCustomFieldsToShow}
              onCancelClick={() => onCancelClick(popoverProps)}
              onSelect={(customFieldId) => {
                onSelect(customFieldId);
                setIsShowingCreateDropdown(false);
                popoverProps.onRequestClose();
              }}
            />
          );
        }
        return (
          <CustomFieldDropdown
            popoverProps={popoverProps}
            customFieldResourceType={CustomFieldResourceTypesEnum.ACCOUNT}
            selectedCustomFieldId={selectedCustomFieldId}
            allSelectedCustomFieldIds={allSelectedCustomFieldIds}
            onSelect={onSelect}
            customFields={customFields}
            onCreateClick={onCreateClick}
            renderBannerText={() => renderBannerText(source)}
            renderCustomFieldName={renderCustomFieldName}
          />
        );
      }}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderMessage(selectedCustomFieldId?: number) {
  if (selectedCustomFieldId !== CrmSyncDefaultFieldMappingIdsEnum.DOMAINS) {
    return null;
  }
  return (
    <Trans id="accounts-crm-dialog-sync-mapping-domain-message">
      Contacts with matching email domains will be auto-associated with their account
    </Trans>
  );
}

function maybeRenderErrorMessage(isFrontCustomFieldMissing?: boolean) {
  if (!isFrontCustomFieldMissing) {
    return undefined;
  }
  return (
    <Trans id="accounts-crm-dialog-sync-mapping-custom-field-deleted">Field has been deleted in Front.</Trans>
  );
}

function renderPlaceholder() {
  return (
    <StyledMappingPlaceholderDiv>
      <Trans id="accounts-crm-dialog-sync-mapping-custom-field-placeholder">
        Select or create field to map to
      </Trans>
    </StyledMappingPlaceholderDiv>
  );
}

function renderCreateDropdownTitle() {
  return <Trans id="accounts-crm-dialog-sync-custom-field-dropdown-title">New account custom field</Trans>;
}

function renderTypesDropdownItemDisabledTooltip(source: CrmSyncSourcesEnum) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Trans id="accounts-crm-dialog-sync-custom-field-create-type-item-tooltip-salesforce">
          Selected Salesforce Data Type cannot be mapped to this Front field type.
        </Trans>
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Trans id="accounts-crm-dialog-sync-custom-field-create-type-item-tooltip-hubspot">
          Selected Hubspot Data Type cannot be mapped to this Front field type.
        </Trans>
      );
    default:
      return null;
  }
}

// TODO: Pass the crm field type to this dropdown.
function renderTypesDisabledTooltip(source: CrmSyncSourcesEnum, type: CustomFieldsEnum) {
  return (
    <>
      <StyledSourceSpan>{source}</StyledSourceSpan> “CRM FIELD TYPE”{' '}
      <Trans id="accounts-crm-dialog-sync-custom-field-create-type-disabled-tooltip">
        Data Type can only be mapped to a Front {renderCustomFieldType(type)} type field.
      </Trans>
    </>
  );
}

function renderBannerText(source: CrmSyncSourcesEnum) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Trans id="accounts-crm-dialog-sync-mapping-custom-field-dropdown-salesforce-banner">
          Only showing Front fields compatible with selected Salesforce field
        </Trans>
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Trans id="accounts-crm-dialog-sync-mapping-custom-field-dropdown-hubspot-banner">
          Only showing Front fields compatible with selected Hubspot field
        </Trans>
      );
    default:
      return null;
  }
}

function renderCustomFieldName(customField: CustomFieldModel) {
  if (customField.id < 0) {
    return renderDefaultCustomFieldName(customField.id);
  }
  return customField.name;
}
